// Tiny router: real URLs (/courses, /articles/abc) when served by server/index.mjs,
// hash URLs (#/courses) when dist-single/index.html is opened from disk.
import { useEffect, useState, type ReactNode } from 'react';

const isFile = () => location.protocol === 'file:';
export const currentPath = () => (isFile() ? location.hash.replace(/^#/, '') || '/' : location.pathname) || '/';
export const hrefFor = (to: string) => (isFile() ? `#${to}` : to);

export function navigate(to: string, opts: { focus?: string } = {}) {
  if (to !== currentPath()) {
    if (isFile()) location.hash = to;
    else history.pushState(null, '', to);
    window.dispatchEvent(new Event('ht:navigate'));
  }
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  window.scrollTo({ top: 0, behavior: 'auto' });
  // Move focus to the new page (or a given field) after it renders, so keyboard and screen-reader users land on it.
  window.setTimeout(() => {
    const el = (opts.focus && document.getElementById(opts.focus)) || document.getElementById('main');
    (el as HTMLElement | null)?.focus({ preventScroll: !opts.focus });
  }, reduce ? 0 : 60);
}

export function usePath() {
  const [path, setPath] = useState(currentPath);
  useEffect(() => {
    const on = () => setPath(currentPath());
    window.addEventListener('popstate', on);
    window.addEventListener('hashchange', on);
    window.addEventListener('ht:navigate', on);
    return () => {
      window.removeEventListener('popstate', on);
      window.removeEventListener('hashchange', on);
      window.removeEventListener('ht:navigate', on);
    };
  }, []);
  return path;
}

export function Link({ to, className, children, current, label, focus }: { to: string; className?: string; children?: ReactNode; current?: boolean; label?: string; focus?: string }) {
  const onClick = (e: MouseEvent) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return; // let "open in new tab" work
    e.preventDefault();
    navigate(to, { focus });
  };
  return (
    <a href={hrefFor(to)} className={className} onClick={onClick} aria-current={current ? 'page' : undefined} aria-label={label}>
      {children}
    </a>
  );
}
